<template>
  <div v-if="!$apollo.loading" class="accreditation-builder">
    <div class="not-available">
      <h1>{{ adminText('workflowsAll.sorry_design_tool_is_currently_only_available_4e5077c', 'Sorry, Design Tool is currently only available for Desktop.') }}</h1>
    </div>
    <div class="design-tool-container">
      <h1 class="builder-title">{{ adminText('workflowsAll.accreditation_design', 'Accreditation Design') }}</h1>
      <div class="builder-columns">
        <div class="builder-editor">
          <div class="side-selector" :aria-label="adminText('workflowsAll.select_accreditation_side_cb6bddb', 'Select accreditation side')">
            <button
              v-for="side in ['front', 'back']"
              :key="side"
              type="button"
              :class="['side-selector__button', { 'side-selector__button--active': activeSide === side }]"
              :aria-pressed="activeSide === side"
              @click="selectSide(side)"
            >
              <span class="side-selector__title">{{ side === 'front' ? adminText('workflowsAll.front_side_9512dc8', 'Front side') : adminText('workflowsAll.back_side_a2c84b7', 'Back side') }}</span>
              <span class="side-selector__format">{{ workflowBuilderFormatLabel(options.format) }}</span>
            </button>
          </div>
          <section :class="['builder-workspace', { 'builder-workspace--back': activeSide === 'back' }]">
            <div class="canvas-area">
              <div class="canvas-stack" :style="{ width: Math.max(396, toPixels(getWidth())) + 'px' }">
                <div
                  :class="['builder-toolbar', { 'builder-toolbar--active': activeItemIndex !== -1 }]"
                >
                  <div class="toolbar-status">
                    <img :src="selectionIcon" width="24" height="24" alt="">
                    <span>{{ workflowLabel(activeItemIndex !== -1 ? adminText('workflowsAll.edit_selected_component_1394f0c', 'Edit selected component') : adminText('workflowsAll.select_a_component_to_adjust_its_style_66e8d9f', 'Select a component to adjust its style')) }}</span>
                  </div>
                  <div v-if="activeItemIndex !== -1" class="toolbar-controls">
                    <text-toolbar v-if="activeToolbar === 'text'" :styles.sync="styles" />
                    <color-toolbar
                      v-if="activeToolbar === 'color'"
                      :styles.sync="styles"
                      :extra-modules="extraModules"
                    />
                  </div>
                </div>
                <div class="canvas-page">
                  <preview
                    v-if="showPreviewDialog"
                    :background-image-url="backgroundImageURL"
                    :items="workflowUi(droppedItems)"
                    :height="toPixels(getHeight())"
                    :width="toPixels(getWidth())"
                    :accreditation="previewData"
                    :attendee-fields="fields"
                    class="drop drop-zone preview-canvas"
                  />
                  <drop
                    v-else
                    :style="[
                      {
                        backgroundSize: 'cover',
                        height: toPixels(getHeight()) + 'px',
                        width: toPixels(getWidth()) + 'px',
                      },
                      backgroundImageURL && !backgroundImageURL.startsWith('data:image')
                        ? { backgroundImage: 'url(' + backgroundImageURL + ')' }
                        : {},
                    ]"
                    class="drop drop-zone"
                    @drop="handleDrop"
                  >
                    <template v-if="renderBuilder">
                      <div :style="{ height: '100%' }">
                        <div
                          v-if="backgroundImageURL && backgroundImageURL.startsWith('data:image')"
                          :style="{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                          }"
                        >
                          <spinner />
                        </div>
                        <vue-draggable-resizable
                          v-for="(item, index) in droppedItems"
                          v-else
                          :ref="`${item.id}`"
                          :key="item.id"
                          :resizable="item.resizable"
                          :w="item.w"
                          :h="item.h"
                          :z="item.active ? 100 : item.z"
                          :x="item.x"
                          :y="item.y"
                          :minw="10"
                          :minh="5"
                          :grid="[GRID_SIZE, GRID_SIZE]"
                          :active.sync="item.active"
                          :handles="['tm', 'ml', 'bm', 'mr']"
                          :class="['parent-container', { 'active-item': item.active }]"
                          :style="`transform: rotate(${item.styles.rotate}deg)`"
                          :parent="true"
                          axis="both"
                          @resizestop="(x, y, w, h) => onResize(index, item.type, { x, y, w, h })"
                          @dragstop="(x, y) => updateItem(index, { x, y })"
                          @activated="() => onFocusItem(item, index)"
                        >
                          <div v-if="item.active" class="item-action delete" @click="deleteItem(item, index)">
                            <v-icon small color="white">close</v-icon>
                          </div>
                          <div v-if="item.active" class="item-action rotate" @click="rotateItem(item, index)">
                            <v-icon small color="white">rotate_right</v-icon>
                          </div>
                          <div
                            v-if="item.active && item.type === 'zone'"
                            class="item-action upload-zone-image"
                            @click="uploadZoneImage(index, item)"
                          >
                            <v-icon small color="white">camera</v-icon>
                          </div>
                          <item :event-id="eventId" :data="workflowUi(item)" class="no-select" />
                        </vue-draggable-resizable>
                      </div>
                    </template>
                  </drop>
                </div>
                <footer class="builder-actions">
                  <main-button
                    class="builder-action-button"
                    flat
                    :text="backgroundImageURL ? adminText('workflowsAll.remove_background_e194638', 'Remove background') : adminText('workflowsAll.add_background_7644fe6', 'Add background')"
                    @click="backgroundImageURL ? backgroundImageURL = null : openFSPicker()"
                  />
                  <div class="builder-actions__group">
                    <v-btn
                      class="builder-icon-button"
                      flat
                      icon
                      :aria-label="showPreviewDialog ? adminText('workflowsAll.continue_editing_b101465', 'Continue editing') : adminText('workflowsAll.preview_f1fbb2b', 'Preview')"
                      :title="showPreviewDialog ? adminText('workflowsAll.continue_editing_b101465', 'Continue editing') : adminText('workflowsAll.preview_f1fbb2b', 'Preview')"
                      :aria-pressed="showPreviewDialog"
                      @click="showPreviewDialog = !showPreviewDialog"
                    >
                      <img :src="eyeIcon" width="24" height="24" alt="">
                    </v-btn>
                    <options-menu
                      :options.sync="options"
                      :has-back-side="hasBackSide"
                      @disable-back-side="disableBackSide"
                    />
                  </div>
                </footer>
              </div>
            </div>
          </section>
          <main-button
            class="save-button"
            depressed
            :loading="saving"
            :text="adminText('workflowsAll.save_changes_179359b', 'Save changes')"
            @click="saveBuilder()"
          />
        </div>
        <aside class="element-library">
          <div class="library-heading">
            <h2>{{ adminText('workflowsAll.components_9289473', 'Components') }}</h2>
            <p>{{ adminText('workflowsAll.drag_components_onto_page', 'Drag components onto the page') }}</p>
          </div>
          <elements :fields="fields" :zones="availableZones" :extra-modules="extraModules" />
        </aside>
      </div>
    </div>
    <simple-snackbar :open.sync="snackbarOpen" color="primary" :message-prop="adminText('workflowsAll.design_saved_d72fbe5', 'Design saved')" />
    <hidden-file-input ref="file-input" max-file-size-mb="10" @file="processBackground($event)" />
    <hidden-file-input ref="upload-zone-image" max-file-size-mb="10" @file="processImage($event)" />
    <cropper-dialog
      :aspect-ratio="getWidth() / getHeight()"
      :image="backgroundImageURL"
      @dismissed="backgroundImageURL = null"
      @cropped="uploadBackground($event)"
    />
  </div>
</template>

<script>
import WorkflowDisplay from '@/admin-i18n/workflows-all-display'
import MainButton from '../shared/main-button.vue'
import selectionIcon from '@/assets/builder/selection.svg'
import eyeIcon from '@/assets/builder/eye.svg'
import { SAVE_BUILDER } from '../../graphql/builder.graphql'
import { GET_EVENT_BUILDER } from '../../graphql/event.graphql'

import {
  toCSS,
  fileToDataUrl,
  getMimeFromDataURL,
  dataURLToImage,
  resizeImage,
  fileToImage,
  objectToArray,
} from './../../utils'
import { getSignedURL, uploadImage } from './../../http'

import Item from './item.vue'
import Preview from './preview.vue'
import ColorToolbar from './color-toolbar.vue'
import TextToolbar from './text-toolbar.vue'
import Elements from './elements.vue'
import OptionsMenu from './options-menu.vue'
import SimpleSnackbar from '../shared/snackbars/simple-snackbar.vue'
import { defaultStyles, formats, mmToPixel, previewData } from './builder.utils'
import {
  getBuilderBackgroundResizeOptions,
  getBuilderImageResizeOptions,
} from './../../builder-image-sizing'

import HiddenFileInput from './../shared/hidden-file-input'
import CropperDialog from './../shared/dialogs/cropper-dialog'
import { getSampleFieldsValue } from './../accreditations/attendee-fields.js'
import Spinner from '../shared/spinner'

const uuidv4 = require('uuid/v4')

export default {
  name: 'accreditation-builder',
  components: {
    MainButton,
    TextToolbar,
    ColorToolbar,
    OptionsMenu,
    Elements,
    Preview,
    Item,
    SimpleSnackbar,
    HiddenFileInput,
    CropperDialog,
    Spinner,
  },
  mixins: [WorkflowDisplay],
  props: {
    eventId: String,
  },
  data() {
    return {
      selectionIcon,
      eyeIcon,
      saving: false,
      defaultStyles,
      extraModules: {},
      renderBuilder: true,
      activeSide: 'front',
      hasBackSide: false,
      designs: {
        front: { backgroundImageURL: null, droppedItems: [], availableZones: [] },
        back: { backgroundImageURL: null, droppedItems: [], availableZones: [] },
      },
      showPreviewDialog: false,
      activeToolbar: '',
      activeItemIndex: -1,
      permission: '',
      styles: {},
      fields: [],
      formats,
      options: { format: formats[0], orientation: formats[0].defaultOrientation },
      previewData: previewData,
      GRID_SIZE: mmToPixel(0.01),
      snackbarOpen: false,
      activeZoneIndex: -1,
    }
  },
  computed: {
    backgroundImageURL: {
      get() {
        return this.designs[this.activeSide].backgroundImageURL
      },
      set(value) {
        this.designs[this.activeSide].backgroundImageURL = value
      },
    },
    droppedItems: {
      get() {
        return this.designs[this.activeSide].droppedItems
      },
      set(value) {
        this.designs[this.activeSide].droppedItems = value
      },
    },
    availableZones: {
      get() {
        return this.designs[this.activeSide].availableZones
      },
      set(value) {
        this.designs[this.activeSide].availableZones = value
      },
    },
  },
  watch: {
    options: {
      handler({ format }) {
        const height = this.toPixels(format.height)
        const width = this.toPixels(format.width)
        const scaleItem = item => this.translateItem(item, width, height)
        Object.keys(this.designs).forEach(side => {
          this.designs[side].droppedItems = this.designs[side].droppedItems.map(scaleItem)
        })
        this.renderBuilder = false
        this.$nextTick(() => {
          this.renderBuilder = true
        })
      },
      deep: true,
    },
    styles: {
      handler(values) {
        const index = this.activeItemIndex
        const item = this.droppedItems[index]
        if (!item) return
        const ref = this.$refs[item.id]
        let size = {}
        if (ref) {
          const { width, height } = ref[0].$el.getBoundingClientRect()
          size = { w: width, h: height }
        }

        if (item.type === 'zone') {
          const indexes = this.droppedItems
            .map(({ type }, i) => ({ type, i }))
            .filter(({ type }) => type === 'zone')
          indexes.forEach(({ i }) => {
            const {
              styles: { backgroundImage },
            } = this.droppedItems[i]
            this.updateItem(i, {
              ...size,
              styles: { ...item.styles, ...values, backgroundImage },
              active: true,
            })
          })
        } else {
          this.updateItem(index, { ...size, styles: { ...item.styles, ...values }, active: true })
        }
      },
      deep: true,
    },
  },
  methods: {
    selectSide(side) {
      if (side === 'back') this.hasBackSide = true
      this.activeSide = side
      this.activeItemIndex = -1
      this.activeToolbar = ''
      this.styles = {}
      this.showPreviewDialog = false
      this.renderBuilder = false
      this.$nextTick(() => {
        this.renderBuilder = true
      })
    },
    enableBackSide() {
      this.selectSide('back')
    },
    disableBackSide() {
      this.hasBackSide = false
      this.selectSide('front')
    },
    async processBackground(file) {
      this.backgroundImageURL = await fileToDataUrl(file)
    },
    getHeight() {
      const { orientation, format } = this.options
      const orientationChanged = orientation !== format.defaultOrientation

      return !orientationChanged ? format.height : format.width
    },
    getWidth() {
      const { orientation, format } = this.options
      const orientationChanged = orientation !== format.defaultOrientation

      return !orientationChanged ? format.width : format.height
    },
    translateItem(item, width, height) {
      const outOfbounds = item.y >= height || item.x >= width
      if (outOfbounds) {
        return {
          ...item,
          x: 0,
          y: 0,
        }
      }
      const w = item.x + item.w > width ? width - item.x : item.w
      const h = item.y + item.h > height ? height - item.y : item.h

      return {
        ...item,
        w,
        h,
      }
    },
    toPixels(mm) {
      return mmToPixel(mm)
    },
    rotateItem({ styles, w, h, ...rest }, index) {
      const alreadyRotated = styles.rotate
      const angle = alreadyRotated ? (styles.rotate + 90) % 360 : 90
      this.updateItem(index, { ...rest, styles: { ...styles, rotate: angle } })
      this.renderBuilder = false
      this.$nextTick(() => {
        this.renderBuilder = true
      })
    },
    onFocusItem(item, index) {
      const { styles, toolbar } = item
      this.styles = styles
      this.activeToolbar = toolbar
      this.activeItemIndex = index
    },
    onResize(index, type, { x, y, w, h }) {
      this.updateItem(index, {
        x,
        y,
        w,
        h,
      })
    },
    updateItem(index, properties) {
      const newItem = {
        ...this.droppedItems[index],
        ...properties,
      }
      this.droppedItems.splice(index, 1, newItem)
    },
    getStyles(data) {
      return toCSS(data)
    },
    deleteItem(item, index) {
      if (item.active) {
        this.activeItemIndex = -1
      }
      if (item.type === 'zone') {
        this.availableZones.push(item.placeholder)
        this.availableZones = this.availableZones.sort((a, b) => a - b)
      }
      this.droppedItems.splice(index, 1)
    },
    handleDrop(transferData, event) {
      const { type, subtype, databaseField, placeholder, styles, toolbar, meta, fields } =
        transferData
      const { height: h, width: w } = styles
      const index = this.droppedItems.length

      this.droppedItems.push({
        id: uuidv4(),
        type,
        subtype,
        placeholder,
        databaseField,
        meta,
        styles,
        toolbar,
        active: true,
        resizable: true,
        fields,
        z: type === 'shape' ? 1 : 2,
        x: event.offsetX,
        y: event.offsetY,
        w,
        h,
      })
      this.onFocusItem({ styles, toolbar }, index)

      if (type === 'zone') {
        const zoneIndex = this.availableZones.indexOf(placeholder)
        this.availableZones.splice(zoneIndex, 1)
      }
    },
    uploadZoneImage(index) {
      this.activeZoneIndex = index
      this.$refs['upload-zone-image'].$refs['input'].click()
    },
    async uploadBackground(dataURL) {
      const mime = getMimeFromDataURL(dataURL)
      const image = await dataURLToImage(dataURL)
      const file = await resizeImage(image, {
        to: 'file',
        mime,
        ...getBuilderBackgroundResizeOptions(this.getWidth(), this.getHeight()),
      })
      const path = `${this.eventId}/avatars/${uuidv4()}`
      const url = await this.uploadImage(file, path)
      this.backgroundImageURL = url
    },
    async uploadImage(file, path) {
      const { upload: uploadURL, download: downloadURL } = await getSignedURL(path)
      await uploadImage(uploadURL, file)

      return downloadURL
    },
    async processImage(f) {
      const image = await fileToImage(f)
      const file = await resizeImage(image, {
        to: 'file',
        mime: f.type,
        ...getBuilderImageResizeOptions(),
      })
      const path = `${this.eventId}/builder/${uuidv4()}`
      const httpURL = await this.uploadImage(file, path)
      const activeZone = this.droppedItems[this.activeZoneIndex]
      const { styles, ...rest } = activeZone
      this.updateItem(this.activeZoneIndex, {
        ...rest,
        styles: { ...styles, backgroundImage: httpURL },
      })
    },
    async saveBuilder() {
      if (this.saving) return
      this.saving = true
      try {
        await this.$apollo.mutate({
          mutation: SAVE_BUILDER,
          variables: {
            eventID: this.eventId,
            items: this.designs.front.droppedItems,
            backgroundImage: this.designs.front.backgroundImageURL,
            backItems: this.designs.back.droppedItems,
            backBackgroundImage: this.designs.back.backgroundImageURL,
            hasBackSide: this.hasBackSide,
            format: this.options.format.name,
            orientation: this.options.orientation,
          },
        })
        this.snackbarOpen = true
      } finally {
        this.saving = false
      }
    },
  },
  apollo: {
    getEvent: {
      query: GET_EVENT_BUILDER,
      variables() {
        return {
          id: this.eventId,
        }
      },
      result({ data, loading }) {
        if (!loading) {
          const { getEvent: event } = data
          this.extraModules = event.extraModules
          this.fields = objectToArray(event.attendeeFields, 'name')
          const allZones = [...Array(event.numberOfZones).keys()].map(z => z + 1)
          if (!event.builder) {
            this.designs.front.availableZones = allZones
            this.designs.back.availableZones = allZones
            const defaultFormat = formats[0]
            this.options = {
              orientation: defaultFormat.defaultOrientation,
              format: defaultFormat,
            }
          } else {
            const items = event.builder.items || []
            const zoneItems = items
              .filter(({ type }) => type === 'zone')
              .map(({ placeholder }) => placeholder)
            const backItems = event.builder.backItems || []
            const backZoneItems = backItems
              .filter(({ type }) => type === 'zone')
              .map(({ placeholder }) => placeholder)
            this.designs.front.availableZones = allZones.filter(x => !zoneItems.includes(x))
            this.designs.back.availableZones = allZones.filter(x => !backZoneItems.includes(x))
            const { format, orientation } = event.builder
            const defaultFormat = formats.find(f => f.name === format)
            this.options = {
              orientation,
              format: defaultFormat,
            }

            this.previewData = {
              ...this.previewData,
              fields: getSampleFieldsValue(event.attendeeFields),
            }
            this.designs.front.backgroundImageURL = event.builder.backgroundImage
            this.designs.front.droppedItems = items
            this.designs.back.backgroundImageURL = event.builder.backBackgroundImage
            this.designs.back.droppedItems = backItems
            this.hasBackSide = Boolean(event.builder.hasBackSide)
          }
        }
      },
    },
  },
}
</script>

<style scoped>
.accreditation-builder {
  --builder-surface: #f5f5f5;
  --builder-primary: #0277bd;
  --builder-text: #333;
  font-family: 'Source Sans Pro', sans-serif;
  color: var(--builder-text);
}
.design-tool-container {
  max-width: 1039px;
  margin: 0 auto;
  padding: 0 0 28px;
}
.builder-title,
.library-heading h2,
.side-selector__title {
  margin: 0;
  font-family: 'Raleway', sans-serif;
  font-size: 20px;
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: -0.6px;
  font-feature-settings: 'lnum' 1, 'pnum' 1;
}
.builder-title { margin-bottom: 24px; }
.builder-columns {
  display: grid;
  grid-template-columns: minmax(0, 685px) 330px;
  align-items: start;
  gap: 24px;
}
.builder-editor { min-width: 0; }
.side-selector { display: flex; }
.side-selector__button {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-height: 94px;
  padding: 24px;
  border: 1px solid var(--builder-surface);
  border-bottom: 0;
  border-radius: 24px 24px 0 0;
  background: #fff;
  color: #979797;
  text-align: left;
  cursor: pointer;
}
.side-selector__button--active {
  background: var(--builder-surface);
  color: var(--builder-text);
}
.side-selector__format {
  font-size: 14px;
  font-weight: 400;
  line-height: 1.2;
  white-space: nowrap;
}
.side-selector__button--active .side-selector__format { color: #666; }
.side-selector__button:focus-visible,
.builder-icon-button:focus-visible {
  outline: 2px solid var(--builder-primary);
  outline-offset: -4px;
}
.builder-workspace {
  background: var(--builder-surface);
  border-radius: 0 24px 24px 24px;
}
.builder-workspace--back { border-top-left-radius: 24px; }
.canvas-area {
  display: flex;
  min-height: 800px;
  padding: 24px;
  overflow-x: auto;
}
.canvas-stack {
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  margin: auto;
}
.builder-toolbar {
  position: relative;
  width: 100%;
  border-radius: 8px;
  background: #e6f1f8;
  color: var(--builder-primary);
}
.builder-toolbar:not(.builder-toolbar--active) { mix-blend-mode: multiply; }
.toolbar-status {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 40px;
  padding: 0 12px 0 16px;
  font-size: 14px;
  line-height: 1.2;
}
.toolbar-status img { flex-shrink: 0; }
.toolbar-controls {
  padding: 8px;
  background: #fff;
  border-radius: 0 0 8px 8px;
}
.canvas-page { position: relative; }
.drop-zone {
  position: relative;
  margin: 0 auto;
  background-color: #fff;
  border: 0;
  border-radius: 8px;
  overflow: hidden;
}
.parent-container {
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px #979797 dashed;
  border-radius: 8px;
}
.active-item { border: 2px var(--builder-primary) dashed !important; }
.item-action {
  position: absolute;
  display: block;
  box-sizing: border-box;
  padding: 1px;
  border: 0;
  border-radius: 50%;
  background: var(--builder-primary);
  font-size: 1px;
  cursor: pointer;
}
.delete { top: -10px; right: -10px; }
.rotate, .upload-zone-image { bottom: -10px; right: -10px; }
.no-select { user-select: none; cursor: grab; }
.builder-actions {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
}
.builder-actions__group { display: flex; align-items: center; gap: 8px; }
.builder-action-button,
.save-button {
  height: 48px;
  min-height: 48px;
  margin: 0 !important;
  padding: 12px 24px;
  border-radius: 24px;
  letter-spacing: -0.27px;
  box-shadow: none !important;
}
.builder-action-button { color: var(--builder-primary) !important; }
.builder-icon-button {
  width: 48px;
  height: 48px;
  margin: 0 !important;
  color: var(--builder-primary) !important;
}
.save-button { margin-top: 56px !important; }
.element-library {
  min-height: 894px;
  padding: 24px;
  border-radius: 24px;
  background: var(--builder-surface);
}
.library-heading { margin-bottom: 24px; }
.library-heading p {
  margin: 4px 0 0;
  color: #666;
  font-size: 16px;
  line-height: 1.5;
}
.not-available { display: none; }
.not-available h1 {
  color: #979797;
  font: 600 20px/24px 'Raleway', sans-serif;
  text-align: center;
}
@media (max-width: 1100px) {
  .builder-columns { grid-template-columns: minmax(0, 1fr) 306px; gap: 16px; }
  .element-library { padding: 20px 16px; }
}
@media (max-width: 800px) {
  .design-tool-container { display: none; }
  .not-available { display: flex; height: calc(100vh - 136px); align-items: center; }
}
</style>
